import t from "../../i18n/i18next";
import { parsePossiblySerializedJson } from "../../utils/httpResponse";

type ErrorDetails = {
    code?: string;
    responseText?: string;
};

const asNonEmptyString = (value: unknown): string | undefined => {
    if (typeof value !== "string") {
        return undefined;
    }
    const trimmed = value.trim();
    return trimmed ? trimmed : undefined;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === "object" && !Array.isArray(value);

const extractFromResponseLike = (value: unknown): ErrorDetails => {
    const parsed = parsePossiblySerializedJson(value, 2);
    if (isRecord(parsed)) {
        return {
            code: asNonEmptyString(parsed.error),
            responseText: asNonEmptyString(parsed.response),
        };
    }

    return {
        responseText: asNonEmptyString(parsed),
    };
};

const extractErrorDetails = (payload: unknown): ErrorDetails => {
    const parsedPayload = parsePossiblySerializedJson(payload, 2);

    if (!isRecord(parsedPayload)) {
        return {
            responseText: asNonEmptyString(parsedPayload),
        };
    }

    const rootRecord = parsedPayload as Record<string, unknown>;
    const rootResponse = extractFromResponseLike(rootRecord.response);

    const nestedErrorResponse = isRecord(rootRecord.error)
        ? extractFromResponseLike(
              (rootRecord.error as Record<string, unknown>).response
          )
        : {};

    return {
        code:
            rootResponse.code ||
            nestedErrorResponse.code ||
            asNonEmptyString(rootRecord.code),
        responseText:
            rootResponse.responseText ||
            nestedErrorResponse.responseText ||
            asNonEmptyString(rootRecord.message) ||
            asNonEmptyString(rootRecord.detail),
    };
};

export const resolveRsaaErrorMessage = (
    payload: unknown,
    fallbackMessage: string
): string => {
    const { code, responseText } = extractErrorDetails(payload);

    if (responseText) {
        return t(responseText);
    }

    if (code) {
        return t(code);
    }

    return fallbackMessage;
};
